import { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

// Address search box backed by the local /geocode/search endpoint (OpenStreetMap).
export const AddressAutocomplete = ({ value, onChange, placeholder, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const focused = useRef(false);
  const skipNext = useRef(false);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 3) { setSuggestions([]); setOpen(false); return; }
    const t = setTimeout(() => {
      api.geocodeSearch(q)
        .then((data) => { setSuggestions(data || []); setOpen(focused.current && (data || []).length > 0); })
        .catch(() => setSuggestions([]));
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => {
    skipNext.current = true;
    onChange(s.label);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={boxRef} className="relative">
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { focused.current = true; if (suggestions.length) setOpen(true); }}
        onBlur={() => { focused.current = false; }}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-popover shadow-md" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={i}
              onClick={() => pick(s)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
              data-testid={`${testId}-option-${i}`}
            >
              <MapPin size={14} className="mt-0.5 shrink-0 text-primary" />
              <span className="line-clamp-2">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
